/**
 * Dashboard Page
 * Actionable overview: attention items (banded), portfolio stats, and pending workflows.
 * Mobile-first: prioritized information hierarchy.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Clock,
  Hourglass,
  ClipboardList,
  CircleCheck,
  LayoutDashboard,
  type LucideIcon,
} from 'lucide-react';
import { formatTimestamp, asIsoDateTimeString } from '@resida/shared';
import { daysOverdue } from '../utils/date';
import { useAuth } from '../hooks/useAuth';
import {
  listPortfolios,
  listProperties,
  getAttentionItems,
  type AttentionItem,
} from '../lib/api';
import { PageSection, ListItem, StatRow, StatCard, EmptyState } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { PendingWorkflowsCard } from '../components/dashboard/PendingWorkflowsCard';
import { ATTENTION_BAND_ORDER } from '@resida/api-types/attention';

/* ── Types ── */

interface DashboardData {
  portfolioCount: number;
  propertyCount: number;
  attentionItems: AttentionItem[];
  attentionVisible: number;
  // BR-37 — counted across ALL preferences (a dismissed/snoozed item still counts toward the
  // stat tile), unlike the bands above which only render `preference === null` items.
  pastDueCount: number;
  reviewCount: number;
  degraded: string[];
}

type AttentionBand = AttentionItem['band'];

/* ── Helpers ── */

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

// No per-item property timezone is available on an AttentionItem (it can span portfolios/
// properties in different states) — the browser's own zone is the least-wrong default for a
// relative "due" label. Still routed through `formatTimestamp` (never a raw
// `.toLocaleString()`/`@deprecated` helper) so the real IANA zone is explicit at the call site.
const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

/* ── Band config (BR-13 — the server assigns `band`; this is display metadata only) ── */

const BAND_LABEL: Record<AttentionBand, string> = {
  act_now: 'Needs attention now',
  waiting_on_other: 'Waiting on others',
  upcoming: 'Coming up',
  backlog: 'When you get to it',
};

const BAND_ICON: Record<AttentionBand, LucideIcon> = {
  act_now: AlertTriangle,
  waiting_on_other: Hourglass,
  upcoming: Clock,
  backlog: ClipboardList,
};

const BAND_COLOR_CLASS: Record<AttentionBand, string> = {
  act_now: 'text-destructive bg-destructive/10',
  waiting_on_other: 'text-info bg-info/10',
  upcoming: 'text-warning bg-warning/10',
  backlog: 'text-muted-foreground bg-muted',
};

/** Trailing context for a row — amount, then overdue/due date, then a bare count (BR-12: never
 *  render a bare count when a richer field is present). Never invents zero for an absent field. */
function AttentionItemTrailing({ item }: { item: AttentionItem }) {
  const dateIso = item.statutoryDeadline ?? item.dueAt;
  if (item.amountCents == null && item.overdueSince == null && dateIso == null && item.count == null) {
    return null;
  }
  return (
    <div className="text-right">
      {item.amountCents != null && (
        <span className="block text-sm font-medium text-foreground tabular-nums">
          {formatCurrency(item.amountCents)}
        </span>
      )}
      {item.overdueSince != null ? (
        <p className="text-xs text-muted-foreground">{daysOverdue(item.overdueSince)}d overdue</p>
      ) : dateIso != null ? (
        <p className="text-xs text-muted-foreground">
          Due {formatTimestamp(asIsoDateTimeString(dateIso), LOCAL_TZ, { month: 'short', day: 'numeric' })}
        </p>
      ) : item.amountCents == null && item.count != null ? (
        <p className="text-xs text-muted-foreground">{item.count}</p>
      ) : null}
    </div>
  );
}

function AttentionBandSection({ band, items }: { band: AttentionBand; items: AttentionItem[] }) {
  if (items.length === 0) return null;
  const Icon = BAND_ICON[band];
  const label = BAND_LABEL[band];

  return (
    <section aria-label={label} data-testid={`dashboard-band-${band}`}>
      <PageSection
        title={label}
        badge={items.length}
        collapsible={band === 'backlog'}
        defaultCollapsed={band === 'backlog'}
        headingLevel={2}
      >
        {items.map((item) => (
          <ListItem
            key={item.taskHash}
            to={item.actionUrl}
            leading={
              <div className={`h-8 w-8 rounded-full flex items-center justify-center ${BAND_COLOR_CLASS[band]}`}>
                <Icon className="h-4 w-4" />
              </div>
            }
            title={item.title}
            subtitle={item.description}
            trailing={<AttentionItemTrailing item={item} />}
            data-testid={`attention-item-${item.taskHash}`}
          />
        ))}
      </PageSection>
    </section>
  );
}

/* ── Component ── */

export function DashboardPage() {
  const { manager } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [portfoliosRes, propertiesRes] = await Promise.all([
        listPortfolios(),
        listProperties(),
      ]);

      const portfolios = portfoliosRes.portfolios;
      const properties = propertiesRes.properties;

      // BR-25 — a failed attention fetch degrades to "no items", never a thrown dashboard.
      // The per-provider `degraded[]` list (still surfaced when the fetch itself succeeds)
      // is the mechanism for a partial result; this is the outer fetch-level fallback.
      const attentionRes = await getAttentionItems().catch(() => null);

      const attentionItems = attentionRes?.items ?? [];
      const attentionSummary = attentionRes?.summary ?? null;
      const degraded = attentionRes?.degraded ?? [];

      setData({
        portfolioCount: portfolios.length,
        propertyCount: properties.length,
        attentionItems,
        attentionVisible: attentionSummary?.visible ?? 0,
        // BR-37 — the dashboard shows only engine output; these tiles are counted from the
        // attention-engine items, not a separate past-due/review-queue fetch.
        pastDueCount: attentionItems.filter((i) => i.providerId === 'past-due-rent').length,
        reviewCount: attentionItems.find((i) => i.providerId === 'expense-review')?.count ?? 0,
        degraded,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  const greeting = getGreeting();
  // BR-13 — grouped by the SERVER-ASSIGNED band; the client never re-derives it. Dismissed/
  // snoozed items stay off the dashboard (they still show on /attention via "Show hidden").
  const visibleAttentionItems = data ? data.attentionItems.filter((i) => i.preference === null) : [];
  const bandGroups = ATTENTION_BAND_ORDER.map((band) => ({
    band,
    items: visibleAttentionItems.filter((i) => i.band === band),
  }));
  const hasAttentionItems = visibleAttentionItems.length > 0;

  // New user — no portfolios and no properties
  const isNewUser = data && data.portfolioCount === 0 && data.propertyCount === 0;

  return (
    <div className="pb-6 max-w-7xl mx-auto" data-testid="dashboard-page">
      {/* ── Greeting ── */}
      <div className="px-[var(--page-padding-x)] pt-5 pb-4">
        <h1 className="text-lg font-semibold text-foreground">
          {greeting}, {manager?.email?.split('@')[0] ?? 'there'}
        </h1>
        {data && (
          <p className="mt-0.5 text-sm text-muted-foreground">
            {data.attentionVisible > 0
              ? `${data.attentionVisible} item${data.attentionVisible > 1 ? 's' : ''} need${data.attentionVisible === 1 ? 's' : ''} your attention.`
              : 'Everything looks good today.'}
          </p>
        )}
      </div>

      {/* ── Error Banner ── */}
      {error && (
        <div className="mx-[var(--page-padding-x)] mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" role="alert">
          <p className="text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchData} className="mt-2">
            Retry
          </Button>
        </div>
      )}

      {/* ── Degraded Notice (BR-25) — a failed provider must never look like "nothing pending" ── */}
      {data && data.degraded.length > 0 && (
        <div
          role="status"
          className="mx-[var(--page-padding-x)] mb-4 rounded-lg border border-warning/30 bg-warning/5 p-3 text-sm"
          data-testid="dashboard-attention-degraded"
        >
          <p className="text-foreground">
            Some items could not be loaded right now, so this list may be incomplete.
          </p>
        </div>
      )}

      {data && (
        <div className="flex flex-col gap-0 md:gap-6 md:grid md:grid-cols-[1fr_320px] md:px-[var(--page-padding-x)]">
          {/* ── Left Column (main content) ── */}
          <div className="flex flex-col gap-0 md:gap-4 min-w-0">
            {/* Attention Items — four bands, in band order (BR-13) */}
            {hasAttentionItems ? (
              bandGroups.map(({ band, items }) => (
                <AttentionBandSection key={band} band={band} items={items} />
              ))
            ) : (
              <div className="flex items-center gap-2 px-[var(--page-padding-x)] py-3 text-sm text-muted-foreground" data-testid="dashboard-all-clear">
                <CircleCheck className="h-4 w-4 text-success" />
                Everything looks good — no items need attention.
              </div>
            )}

            {/* New User Empty State */}
            {isNewUser && (
              <EmptyState
                icon={LayoutDashboard}
                title="Welcome to your dashboard"
                description="Get started by creating a portfolio and adding your first property."
                action={
                  <Button asChild>
                    <Link to="/portfolios">Create Portfolio</Link>
                  </Button>
                }
                data-testid="dashboard-empty-state"
              />
            )}

            {/* Portfolio Stats */}
            {!isNewUser && (
              <div className="py-4 px-[var(--page-padding-x)] md:px-0">
                <StatRow>
                  <StatCard
                    label="Past Due"
                    value={data.pastDueCount}
                    color={data.pastDueCount > 0 ? 'error' : 'default'}
                    onClick={() => navigate('/reports/analytics')}
                    data-testid="dashboard-stat-past-due"
                  />
                  <StatCard
                    label="Review"
                    value={data.reviewCount}
                    color={data.reviewCount > 0 ? 'warning' : 'default'}
                    onClick={() => navigate('/expenses/confirm')}
                    data-testid="dashboard-stat-review"
                  />
                </StatRow>
              </div>
            )}
          </div>

          {/* ── Right Column (pending workflows — desktop sidebar, mobile below) ── */}
          <div className="md:sticky md:top-4 md:self-start">
            <PendingWorkflowsCard />
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Loading Skeleton ── */

function DashboardSkeleton() {
  return (
    <div className="pb-6 max-w-7xl mx-auto" data-testid="dashboard-page">
      <div className="px-[var(--page-padding-x)] pt-5 pb-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-64 mt-2" />
      </div>
      <div className="px-[var(--page-padding-x)] space-y-3">
        <Skeleton className="h-12 w-full rounded-lg" />
        <Skeleton className="h-12 w-full rounded-lg" />
        <Skeleton className="h-12 w-full rounded-lg" />
      </div>
      <div className="px-[var(--page-padding-x)] mt-6 grid grid-cols-4 gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-16 rounded-lg" />
        ))}
      </div>
    </div>
  );
}

/* ── Helpers ── */

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export default DashboardPage;
